<script setup>
import { ref, computed, onMounted } from 'vue'
import { planningApi } from '@/apis/planning.api'
import { getI18n } from '@/plugins/i18n'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import WarehouseNameLabel from '@/components/shared/WarehouseNameLabel.vue'

defineProps({
  warehouseIds: { type: Array, default: () => [] },
  periodType: { type: String, default: 'all' },
  periodFrom: { type: String, default: null },
  periodTo: { type: String, default: null },
  showWarehouseFilter: { type: Boolean, default: true },
  showPeriodFilter: { type: Boolean, default: true },
  back: { type: Function, required: true },
  next: { type: Function, default: null },
  finish: { type: Function, default: null },
  finishLabel: { type: String, default: null },
  iconRight: { type: String, default: undefined },
})

const emit = defineEmits(['update:warehouseIds', 'update:periodType', 'update:periodFrom', 'update:periodTo'])

const { t } = getI18n()
const warehouses = ref([])

onMounted(async () => {
  const res = await planningApi.getWarehousesAsync({ all: true })
  warehouses.value = res.data
})

const PERIOD_TYPE_OPTS = [
  { type: 'week',    labelKey: 'dashboard.filterPeriodWeek',    icon: 'mdi-calendar-week' },
  { type: 'month',   labelKey: 'dashboard.filterPeriodMonth',   icon: 'mdi-calendar-month' },
  { type: 'quarter', labelKey: 'dashboard.filterPeriodQuarter', icon: 'mdi-calendar-range' },
  { type: 'year',    labelKey: 'dashboard.filterPeriodYear',    icon: 'mdi-calendar-blank-multiple' },
  { type: 'custom',  labelKey: 'common.custom',                 icon: 'mdi-calendar-edit' },
]

const periodOptions = computed(() =>
  PERIOD_TYPE_OPTS.map(o => ({ value: o.type, label: t(o.labelKey), icon: o.icon }))
)
</script>

<template>
  <div>
    <template v-if="showWarehouseFilter">
      <div class="text-caption text-grey-5 text-uppercase q-mb-sm" style="letter-spacing: 0.06em;">
        {{ t('dashboard.filterWarehouse') }}
      </div>
      <q-select
        :model-value="warehouseIds"
        :options="warehouses"
        :label="t('common.warehouse')"
        outlined
        dense
        multiple
        use-chips
        emit-value
        map-options
        option-value="id"
        option-label="name"
        behavior="menu"
        clearable
        class="q-mb-md"
        @update:model-value="emit('update:warehouseIds', $event ?? [])"
      >
        <template #option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section>
              <warehouse-name-label :warehouse="scope.opt" />
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </template>

    <template v-if="showPeriodFilter">
      <q-select
        :model-value="periodType"
        :options="periodOptions"
        :label="t('dashboard.filterPeriod')"
        outlined
        dense
        emit-value
        map-options
        option-value="value"
        option-label="label"
        behavior="menu"
        class="q-mb-sm"
        @update:model-value="emit('update:periodType', $event)"
      >
        <template #selected-item="{ opt }">
          <q-icon :name="opt.icon" size="1rem" class="q-mr-xs" />
          {{ opt.label }}
        </template>
        <template #option="{ itemProps, opt }">
          <q-item v-bind="itemProps">
            <q-item-section avatar>
              <q-icon :name="opt.icon" size="1rem" />
            </q-item-section>
            <q-item-section>{{ opt.label }}</q-item-section>
          </q-item>
        </template>
      </q-select>

      <div v-if="periodType === 'custom'" class="row q-gutter-sm q-mb-sm">
        <q-input
          :model-value="periodFrom"
          type="date"
          :label="t('common.startDate')"
          outlined
          dense
          class="col"
          @update:model-value="emit('update:periodFrom', $event)"
        />
        <q-input
          :model-value="periodTo"
          type="date"
          :label="t('common.endDate')"
          outlined
          dense
          class="col"
          @update:model-value="emit('update:periodTo', $event)"
        />
      </div>
    </template>

    <stepper-navigation
      :back="back"
      :continue="next"
      :finish="finish"
      :finish-label="finishLabel"
      :icon-right="iconRight"
    />
  </div>
</template>

